import { apiFetch } from '@/lib/api';
import type { BillingPlanDto } from './BillingService';

export interface AdminPlanCreateInput {
  key: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  displayOrder?: number;
  stripeProductId?: string | null;
}

export interface AdminPlanUpdateInput {
  name?: string;
  description?: string | null;
  isActive?: boolean;
  displayOrder?: number;
  stripeProductId?: string | null;
}

export interface AdminPriceInput {
  interval: 'month' | 'year';
  currency: string;
  unitAmount: number;
  stripePriceId?: string | null;
  paymentLinkUrl?: string | null;
  isActive?: boolean;
  trialDays?: number;
}

export interface AdminPriceUpdateInput {
  currency?: string;
  unitAmount?: number;
  stripePriceId?: string | null;
  paymentLinkUrl?: string | null;
  isActive?: boolean;
  trialDays?: number;
}

export interface AdminLimitInput {
  limitKey: string;
  limitValue: number;
  isHardLimit?: boolean;
}

export interface AdminLimitUpdateInput {
  limitValue?: number;
  isHardLimit?: boolean;
}

export interface AdminFlagInput {
  flagKey: string;
  flagValue: unknown;
}

export interface AdminFlagUpdateInput {
  flagValue: unknown;
}

export interface AdminFeatureInput {
  featureText: string;
  isIncluded?: boolean;
  category?: string | null;
  displayOrder?: number;
  tooltipHtml?: string | null;
}

export interface AdminFeatureUpdateInput {
  featureText?: string;
  isIncluded?: boolean;
  category?: string | null;
  displayOrder?: number;
  tooltipHtml?: string | null;
}

export interface AdminTransactionQuery {
  orgId?: string;
  status?: 'succeeded' | 'failed' | 'refunded' | 'pending';
  limit?: number;
  offset?: number;
}

export interface AdminTransactionDto {
  id: string;
  organization_id: string;
  stripe_invoice_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  amount: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'refunded' | 'pending';
  paid_at: string | null;
  next_payment_attempt_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  plan_key: string | null;
  plan_name: string | null;
  price_interval: 'month' | 'year' | null;
  price_unit_amount: number | null;
}

export interface AdminTransactionList {
  items: AdminTransactionDto[];
  total: number;
}

export interface AdminOverrideInput {
  planId: string;
  priceId?: string | null;
  prorationBehavior?: 'create_prorations' | 'none';
  cancelAtPeriodEnd?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { message?: string };
}

export class AdminBillingService {
  private readonly baseUrl: string;

  constructor() {
    const vURL = (import.meta as any).env?.VITE_API_URL as string | undefined;
    this.baseUrl = vURL ? vURL.replace(/\/$/, '') : '';
  }

  async listPlans(): Promise<BillingPlanDto[]> {
    return this.request<BillingPlanDto[]>('/api/admin/billing/plans');
  }

  async createPlan(input: AdminPlanCreateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>('/api/admin/billing/plans', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updatePlan(id: string, input: AdminPlanUpdateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async createPrice(planId: string, input: AdminPriceInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/plans/${planId}/prices`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updatePrice(priceId: string, input: AdminPriceUpdateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/prices/${priceId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async createLimit(planId: string, input: AdminLimitInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/plans/${planId}/limits`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateLimit(limitId: string, input: AdminLimitUpdateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/limits/${limitId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async createFlag(planId: string, input: AdminFlagInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/plans/${planId}/flags`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateFlag(flagId: string, input: AdminFlagUpdateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/flags/${flagId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async createFeature(planId: string, input: AdminFeatureInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/plans/${planId}/features`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateFeature(featureId: string, input: AdminFeatureUpdateInput): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/features/${featureId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async deleteFeature(featureId: string): Promise<BillingPlanDto> {
    return this.request<BillingPlanDto>(`/api/admin/billing/features/${featureId}`, {
      method: 'DELETE',
    });
  }

  async listTransactions(query: AdminTransactionQuery = {}): Promise<AdminTransactionList> {
    const params = new URLSearchParams();
    if (query.orgId) params.set('orgId', query.orgId);
    if (query.status) params.set('status', query.status);
    if (typeof query.limit === 'number') params.set('limit', String(query.limit));
    if (typeof query.offset === 'number') params.set('offset', String(query.offset));

    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<AdminTransactionList>(`/api/admin/billing/transactions${suffix}`);
  }

  async overrideSubscription(orgId: string, input: AdminOverrideInput): Promise<any> {
    return this.request(`/api/admin/billing/orgs/${orgId}/override`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await apiFetch(`${this.baseUrl}${path}` , {
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });

    const payload = (await res.json()) as ApiResponse<T>;
    if (!res.ok || !payload.success) {
      throw new Error(payload?.error?.message || 'Request failed');
    }
    return payload.data as T;
  }
}
